// react
import { useCallback, useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// feature-hooks
import useResponsiveChartHeight from '@/features/energyxplorer/hooks/useResponsiveChartHeight';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-more';
import 'highcharts/modules/no-data-to-display';

const HourAnalysisChart = ({
  data,
  viewMode,
  isLoading,
  unit = 'kWh',
  querySkipped = false,
}) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartHeight = useResponsiveChartHeight();

  const themeOptions = useMemo(
    () => (theme.palette.mode === 'dark' ? darkTheme : lightTheme),
    [theme.palette.mode],
  );

  // Imperative loading overlay
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
    if (isLoading && !querySkipped) {
      chart.showLoading(`${t('common.loading_data')}`);
      chart.hideNoData();
    } else chart.hideLoading();
  }, [isLoading, querySkipped, t]);

  const xCategories = useMemo(
    () => Array.from({ length: 24 }, (_, i) => `${i}:00`),
    [],
  );

  // Build the series data depending on viewMode
  const seriesData = useMemo(() => {
    if (viewMode === 'stacked_column') {
      const hourStackedData = data?.HourStackedData || [];

      // Collect all unique children
      const childrenMap = new Map();
      hourStackedData.forEach((hourData) => {
        hourData.ChildContributions?.forEach((child) => {
          if (!childrenMap.has(child.ChildId)) {
            childrenMap.set(child.ChildId, {
              id: child.ChildId,
              name: child.ChildName,
              data: Array(24).fill(null),
            });
          }
        });
      });

      // Create series for not analyzed data
      const notAnalyzedSeries = {
        id: 'not-analyzed',
        name: t('common.not_analyzed', 'Ikke Analyseret'),
        data: Array(24).fill(null),
        color:
          theme.palette.mode === 'dark'
            ? theme.palette.grey[700]
            : theme.palette.grey[300],
      };

      // Fill in the data for each child and not analyzed
      hourStackedData.forEach((hourData) => {
        const h = hourData?.HourOfDay;
        if (h >= 0 && h < 24) {
          hourData.ChildContributions?.forEach((child) => {
            const series = childrenMap.get(child.ChildId);
            if (series) {
              series.data[h] = child.MeanValue ?? null;
            }
          });

          // Add not analyzed value
          notAnalyzedSeries.data[h] = hourData.NotAnalyzedMeanValue ?? null;
        }
      });

      const seriesArr = [
        notAnalyzedSeries,
        ...Array.from(childrenMap.values()),
      ];

      // ✅ If every point in every series is null -> return [] (triggers no-data)
      const hasAnyValue = seriesArr.some((s) => s.data?.some((v) => v != null));
      return hasAnyValue ? seriesArr : [];
    }

    const arr = Array(24).fill(null);
    const hourAnalysisData = data?.HourAnalysisData || [];

    if (viewMode === 'column') {
      hourAnalysisData.forEach((item) => {
        const h = item?.HourOfDay;
        if (h >= 0 && h < 24) arr[h] = item?.MeanValue ?? null;
      });

      // ✅ If all null -> return []
      return arr.some((v) => v != null) ? arr : [];
    }

    // boxplot
    hourAnalysisData.forEach((item) => {
      const h = item?.HourOfDay;
      if (h < 0 || h >= 24) return;

      const ok =
        item?.MinValue != null &&
        item?.Q1Value != null &&
        item?.MedianValue != null &&
        item?.Q3Value != null &&
        item?.MaxValue != null;

      arr[h] = ok
        ? [
            item.MinValue,
            item.Q1Value,
            item.MedianValue,
            item.Q3Value,
            item.MaxValue,
          ]
        : null;
    });

    // ✅ If all null -> return []

    return arr.some((v) => v != null) ? arr : [];
  }, [data, t, theme, viewMode]);

  // Stable tooltip formatter (captures viewMode/unit/categories)
  const tooltipFormatter = useCallback(
    function () {
      const hourLabel = xCategories[this.point?.index] ?? this.x;

      if (viewMode === 'box') {
        return `
          <b>${hourLabel}</b><br/>
          <span style="color:#888">Min:</span> ${formatNumber(
            this.point.low,
          )} ${unit}<br/>
          <span style="color:#888">Q1:</span> ${formatNumber(
            this.point.q1,
          )} ${unit}<br/>
          <span style="color:#888">Median:</span> ${formatNumber(
            this.point.median,
          )} ${unit}<br/>
          <span style="color:#888">Q3:</span> ${formatNumber(
            this.point.q3,
          )} ${unit}<br/>
          <span style="color:#888">Max:</span> ${formatNumber(
            this.point.high,
          )} ${unit}
        `;
      }

      return `<b>${hourLabel}</b><br/><span style="color:${this.color}">\u25CF</span> ${this.series.name}: ${formatNumber(
        this.y,
      )} ${unit}`;
    },
    [viewMode, unit, xCategories],
  );

  const seriesName = useMemo(() => {
    if (viewMode === 'box') return 'BoxPlot';
    return data?.Name ?? t('common.mean');
  }, [viewMode, data?.Name, t]);

  const chartOptions = useMemo(() => {
    const isBox = viewMode === 'box';
    const isStacked = viewMode === 'stacked_column';

    return {
      lang: {
        noData: `${querySkipped ? `${t('common.invalid_filter_combination')}` : ''}`,
      },
      accessibility: {
        enabled: false,
      },
      noData: {
        style: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
        },
      },
      loading: {
        labelStyle: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        style: {
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        showDuration: 150,
        hideDuration: 150,
      },
      chart: {
        type: isBox ? 'boxplot' : 'column',
        height: chartHeight,
        backgroundColor: themeOptions.chart.backgroundColor,
        events: {
          load() {
            const chart = this;

            // Ensure we don't attach multiple listeners (React rerenders / remounts)
            if (chart.__mouseleaveHandler) return;

            chart.__mouseleaveHandler = () => {
              chart.tooltip?.hide(0);
              chart.pointer?.reset?.(true); // clears hover state
              chart.xAxis?.forEach((a) => a.hideCrosshair?.());
              chart.yAxis?.forEach((a) => a.hideCrosshair?.());
            };

            chart.container.addEventListener(
              'mouseleave',
              chart.__mouseleaveHandler,
            );
          },
          destroy() {
            const chart = this;
            if (chart.__mouseleaveHandler) {
              chart.container.removeEventListener(
                'mouseleave',
                chart.__mouseleaveHandler,
              );
              chart.__mouseleaveHandler = null;
            }
          },
        },
      },
      title: { text: '' },
      credits: { enabled: false },
      time: { useUTC: true },

      xAxis: {
        categories: xCategories,
        title: { text: t('hour_analysis.x_axis_title') },
        gridLineColor: 'transparent',
        visible: seriesData.length > 0,
        lineColor: themeOptions.xAxis.lineColor,
        labels: { style: { color: themeOptions.xAxis.labels.style.color } },
      },

      yAxis: {
        title: { text: unit },
        gridLineColor: 'transparent',
        visible: seriesData.length > 0,
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
      },

      legend: {
        enabled: isStacked,
        itemStyle: { color: themeOptions.legend.itemStyle.color },
      },

      tooltip: {
        useHTML: true,
        formatter: tooltipFormatter,
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
      },

      plotOptions: {
        column: {
          borderWidth: 0,
          stacking: isStacked ? 'normal' : undefined,
        },
        boxplot: {
          fillColor:
            theme.palette.mode === 'dark'
              ? 'hsla(0, 0%, 19%, 0.10)'
              : 'hsla(0, 0%, 95%, 0.20)',
          lineWidth: 1.5,
        },
      },

      series: isStacked
        ? seriesData.map((s) => ({
            name: s.name,
            type: 'column',
            data: s.data,
            ...s,
          }))
        : [
            {
              name: seriesName,
              type: isBox ? 'boxplot' : 'column',
              data: seriesData,
            },
          ],
    };
  }, [
    viewMode,
    querySkipped,
    theme.palette.mode,
    chartHeight,
    themeOptions.chart.backgroundColor,
    themeOptions.xAxis.lineColor,
    themeOptions.xAxis.labels.style.color,
    themeOptions.yAxis.labels.style.color,
    themeOptions.legend.itemStyle.color,
    themeOptions.tooltip.backgroundColor,
    themeOptions.tooltip.borderColor,
    xCategories,
    t,
    seriesData,
    unit,
    tooltipFormatter,
    seriesName,
  ]);
  // if (seriesData.slice(1).every((v) => v == null)) {
  //   const value = viewMode !== 'column' ? seriesData[0][2] : seriesData[0];
  //   return (
  //     <Alert
  //       severity="info"
  //       sx={{
  //         flexGrow: 1,
  //         borderLeft: (theme) => `4px solid ${theme.palette.info.main}`,
  //       }}
  //     >
  //       <AlertTitle sx={{ fontWeight: 600 }}>
  //         {t('hour_analysis.day_agg_data_title')}
  //       </AlertTitle>
  //       {t('hour_analysis.day_agg_data_description', {
  //         consumption: formatNumber(value),
  //         unit: unit,
  //       })}
  //     </Alert>
  //   );
  // }
  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      immutable={true} // must be immutable
      ref={chartComponentRef}
    />
  );
};

export default HourAnalysisChart;
